// Description: Utility functions for exporting user's data
import logger from './logger.js';

const formatDate = (dateValue) => {
  if (!dateValue) return '';

  try {
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return '';

    return date.toISOString();
  } catch (error) {
    logger.error('Error formatting date:', error);
    return '';
  }
};

const formatArray = (arrayValue) => {
  if (!arrayValue) return '';

  if (Array.isArray(arrayValue)) {
    return arrayValue.join(';');
  } else {
    return typeof arrayValue === 'string' ? arrayValue : String(arrayValue || '');
  }
};

const sanitizeString = (value) => {
  if (value === null || value === undefined) return '';

  const stringValue = String(value);
  if (stringValue.includes(',') || stringValue.includes('"')) {
    return `"${stringValue.replace(/"/g, '""')}"`;
  }

  return stringValue;
};

const parseNameFromEmail = (email) => {
  if (!email) return { firstName: '', lastName: '' };

  try {
    const namePart = email.split('@')[0];
    if (namePart.includes('.')) {
      const [firstName, lastName] = namePart.split('.');
      return {
        firstName: firstName.charAt(0).toUpperCase() + firstName.slice(1),
        lastName: lastName.charAt(0).toUpperCase() + lastName.slice(1)
      };
    } else {
      return {
        firstName: namePart.charAt(0).toUpperCase() + namePart.slice(1),
        lastName: ''
      };
    }
  } catch (error) {
    logger.error("Error parsing name from email:", error);
    return { firstName: '', lastName: '' };
  }
};

const flattenObject = (obj, prefix = '') => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return { [prefix]: obj };
  }

  return Object.keys(obj).reduce((acc, key) => {
    const newPrefix = prefix ? `${prefix}.${key}` : key;

    if (obj[key] && typeof obj[key] === 'object' && !Array.isArray(obj[key]) && Object.keys(obj[key]).length > 0) {
      Object.assign(acc, flattenObject(obj[key], newPrefix));
    } else {
      acc[newPrefix] = obj[key];
    }

    return acc;
  }, {});
};

const determineExportFields = (userData, options = {}) => {
  const fieldGroups = {
    identityFields: [
      '_id',
      'email',
      'firstName',
      'lastName',
      'preferredName'
    ],
    activityFields: [
      'lastLogin'
    ],
    permissionFields: [
      'permissions',
      'groups',
      'sendgridpermissions',
      'dashboardpermissions',
      'twitterxpermissions',
      'entitlementPermissions',
      'intercomPermissions',
      'toolspermissions',
      'mediarelationspermissions',
      'companiespermissions'
    ]
  };

  if (options.fieldGroups) {
    Object.keys(options.fieldGroups).forEach(groupName => {
      fieldGroups[groupName] = [
        ...(fieldGroups[groupName] || []),
        ...options.fieldGroups[groupName]
      ];
    });
  }

  const fieldFormatters = {
    dateFields: ['lastLogin'],
    arrayFields: [...fieldGroups.permissionFields]
  };

  if (options.fieldFormatters) {
    Object.keys(options.fieldFormatters).forEach(formatterName => {
      fieldFormatters[formatterName] = [
        ...(fieldFormatters[formatterName] || []),
        ...options.fieldFormatters[formatterName]
      ];
    });
  }

  if (!options.restrictToFieldGroups && userData && userData.length > 0) {
    const flattenedUsers = userData.map(user => flattenObject(user));

    const allExistingFields = new Set();
    flattenedUsers.forEach(user => {
      Object.keys(user).forEach(field => allExistingFields.add(field));
    });

    const allDefinedFields = new Set();
    Object.values(fieldGroups).forEach(group =>
      group.forEach(field => allDefinedFields.add(field))
    );

    const missingFields = [...allExistingFields].filter(field => !allDefinedFields.has(field));

    if (missingFields.length > 0) {
      fieldGroups.dynamicFields = missingFields;
    }
  }

  let allFields;
  if (options.restrictToFieldGroups) {
    allFields = [];

    const targetGroups = options.targetFieldGroups || Object.keys(fieldGroups);

    targetGroups.forEach(groupName => {
      if (fieldGroups[groupName]) {
        allFields.push(...fieldGroups[groupName]);
      }
    });

    allFields = [...new Set(allFields)];
  } else {
    allFields = Array.from(new Set(
      Object.values(fieldGroups).flat()
    ));
  }

  return {
    fieldGroups,
    fieldFormatters,
    allFields
  };
};

const convertToCSV = (userData, options = {}) => {
  if (!userData || !Array.isArray(userData) || userData.length === 0) {
    return '';
  }

  const { fieldFormatters, allFields } = determineExportFields(userData, options);

  const header = allFields.join(',');

  const rows = userData.map(user => {
    const { firstName, lastName } = parseNameFromEmail(user.email);
    const enhancedUser = {
      ...user,
      firstName: user.firstName || firstName,
      lastName: user.lastName || lastName
    };

    const values = allFields.map(field => {
      let value = enhancedUser[field];

      if (fieldFormatters.dateFields && fieldFormatters.dateFields.includes(field)) {
        return formatDate(value);
      } else if (fieldFormatters.arrayFields && fieldFormatters.arrayFields.includes(field)) {
        return sanitizeString(formatArray(value));
      } else {
        return sanitizeString(value);
      }
    });

    return values.join(',');
  });

  return [header, ...rows].join('\n');
};

export const downloadCSV = (csvContent, filename) => {
  if (!csvContent) {
    logger.error('No CSV content to download');
    return;
  }

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');

  link.setAttribute('href', url);
  link.setAttribute('download', filename || `user-export-${new Date().toISOString().split('T')[0]}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

export const handleUserExport = (userData, selectedUserEmails = [], filename = '', options = {}) => {
  try {
    if (!userData || !Array.isArray(userData)) {
      logger.error('Invalid user data for export');
      return false;
    }

    let csvContent;
    let defaultFilename;
    const timestamp = new Date().toISOString().split('T')[0];

    if (selectedUserEmails.length > 0) {
      const filteredUsers = userData.filter(user =>
        selectedUserEmails.includes(user.email)
      );
      csvContent = convertToCSV(filteredUsers, options);
      defaultFilename = `selected-users-${timestamp}.csv`;
    } else {
      csvContent = convertToCSV(userData, options);
      defaultFilename = `all-users-${timestamp}.csv`;
    }

    if (!csvContent) {
      logger.error('Failed to generate CSV content');
      return false;
    }

    downloadCSV(csvContent, filename || defaultFilename);
    return true;
  } catch (error) {
    logger.error('Error in user export:', error);
    return false;
  }
};
